// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import constants from "@store/constants";
import { useUserNotificationPreferencesContext } from "@store/Framework/Components/UserNotificationPreferencesContext/UserNotificationPreferencesContext";
import { getCustomerInfo } from "@store/Framework/Services/InviteYourColleagueService";
import {
    getUserNotificationPreferences,
    sendUserNotificationPreferences,
} from "@store/Framework/Services/UserProfileService";
import NotificationPreferencesComponent from "@store/Library/Components/NotificationPreferences";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { css, useTheme } from "styled-components";

const enum fields {
    manageSubscriptionsVerbiage = "manageSubscriptionsVerbiage",
    followNewPrice = "followNewPrice",
    followNewFeatures = "followNewFeature",
    followNewTec = "followNewTec",
    followUpcomingShipment = "followUpcomingShipment",
    followOrderShipped = "followOrderShipped",
    followOrderPickup = "followOrderPickup",
    followAccountActivationInvitation = "followAccountActivationInvitation",
    forceModalOpen = "forceModalOpen",
    followOrderChange = "followOrderChange",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.manageSubscriptionsVerbiage]: string;
        [fields.followNewPrice]: boolean;
        [fields.followNewFeatures]: boolean;
        [fields.followNewTec]: boolean;
        [fields.followUpcomingShipment]: boolean;
        [fields.followOrderShipped]: boolean;
        [fields.followOrderPickup]: boolean;
        [fields.followAccountActivationInvitation]: boolean;
        [fields.forceModalOpen]: boolean;
        [fields.followOrderChange]: boolean;
    };
}

type Props = OwnProps;

interface ManageNotificationPreferencesStyles {
    wrapper?: InjectableCss;
    infosBoxWrapper?: InjectableCss;
    inviteCollegeButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    inviteColleagueModal?: ModalPresentationProps;
}

const manageNotificationPreferencesStyles: ManageNotificationPreferencesStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(32)};
        `,
    },
    inviteCollegeButton: {
        variant: "tertiary",
    },
    saveButton: {
        mergeCss: true,
        css: css`
            margin: 0 ${pxToRem(8)} ${pxToRem(8)} 0;
        `,
    },

    inviteColleagueModal: { sizeVariant: "medium" },
};

const styles = manageNotificationPreferencesStyles;

const ManageNotificationPreferences = ({ fields, id }: Props) => {
    const {
        isLoading,
        notificationsFollowed,
        customerSoldTo,
        responseCode,
        errorMessage,
        updateSuccessful,
        updateErrorMessage,
        setIsLoading,
        setResponseCode,
        setErrorMessage,
        setNotificationsFollowed,
        setCustomerSoldTo,
        setUpdateSuccessful,
        setUpdateErrorMessage,
    } = useUserNotificationPreferencesContext();
    const theme = useTheme();
    const { h } = useQueryString();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(fields.forceModalOpen);
    const [mappedfields, setMappedfields] = useState<Dictionary<any>>(fields);

    useEffect(() => {
        if (h) {
            getUserNotificationPreferences({ hash: h }).then(response => {
                setIsLoading(false);
                setResponseCode(response.responseCode);
                setErrorMessage(response.errorMessage);
                setNotificationsFollowed(response.notificationsFollowed || null);
                if (response?.notificationsFollowed?.accountActivated) {
                    setMappedfields({ ...fields, followAccountActivationInvitation: undefined });
                }
            });
            getCustomerInfo({ hash: h }).then(response => {
                setCustomerSoldTo(response);
            });
        }

        if (!h) {
            setIsLoading(false);
            setResponseCode("error");
            setErrorMessage(siteMessage("UserHash_UserNotFound").toString());
        }
    }, []);

    const saveButtonHandler = () => {
        if (h && notificationsFollowed) {
            setIsLoading(true);
            sendUserNotificationPreferences({
                hash: h,
                notificationsFollowed,
            }).then(response => {
                setIsLoading(false);
                if (response.responseCode === "success") {
                    setUpdateSuccessful && setUpdateSuccessful(true);
                }
                if (response.responseCode === "error") {
                    setUpdateErrorMessage && response.errorMessage;
                }
            });
        }
    };

    const linkInErrorMessage = errorMessage ? errorMessage.match(constants.regexPatterns.email) : undefined;
    const linkInlineStyles = `color: ${theme.colors.text.link};`;

    const modalCloseHandler = () => setIsModalOpen(false);

    return (
        <LoadingOverlay loading={isLoading}>
            <Modal
                {...styles.inviteColleagueModal}
                headline={translate("Invite a collegue to Greif+")}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
            >
                <Zone zoneName="InviteColleagueZone" contentId={id} />
            </Modal>
            {responseCode === "success" && notificationsFollowed && (
                <StyledWrapper {...styles.wrapper}>
                    {updateSuccessful && (
                        <InfosBox type="success">{siteMessage("NotificationPreferences_UpdatedSuccessfully")}</InfosBox>
                    )}
                    {updateErrorMessage && <InfosBox type="success">{updateErrorMessage}</InfosBox>}
                    <Typography>{fields.manageSubscriptionsVerbiage}</Typography>
                    <NotificationPreferencesComponent
                        fields={mappedfields}
                        preferences={notificationsFollowed}
                        setValues={setNotificationsFollowed}
                    />
                    <StyledWrapper>
                        <Button {...styles.saveButton} onClick={saveButtonHandler} disabled={isLoading}>
                            {translate("Save preferences")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {responseCode === "error" && errorMessage && (
                <StyledWrapper {...styles.infosBoxWrapper}>
                    <InfosBox type="error">
                        {linkInErrorMessage
                            ? parse(
                                  `<span>${errorMessage.replace(
                                      linkInErrorMessage[0],
                                      `<a href="mailto:${linkInErrorMessage[0]}" style="${linkInlineStyles}">${linkInErrorMessage[0]}</a>`,
                                  )}</span>`,
                              )
                            : errorMessage}
                    </InfosBox>
                </StyledWrapper>
            )}
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: ManageNotificationPreferences,
    definition: {
        allowedContexts: [constants.pageType.notificationPreferences],
        group: constants.widgetGroup.notificationPreferences,
        displayName: "Manage Notification Preferences",
        fieldDefinitions: [
            /* Greif Custom Code - Start */
            {
                name: fields.manageSubscriptionsVerbiage,
                displayName: "Edit instructions (under title text)",
                editorTemplate: "TextField",
                defaultValue: "Choose which email notifications you'd like to receive.",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.followAccountActivationInvitation,
                displayName: "See New Account Activation Invitations Updates",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.followNewPrice,
                displayName: "See New Price Update",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.followNewFeatures,
                displayName: "See New Features Update",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.followNewTec,
                displayName: "See Tec Update",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.followUpcomingShipment,
                displayName: "See Upcoming Shipment",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.followOrderShipped,
                displayName: "See Order Shipped",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.followOrderPickup,
                displayName: "See Order Picked up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.forceModalOpen,
                displayName: "Force Modal Open",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.followOrderChange,
                displayName: "See Order Change",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            /* Greif Custom Code - End */
        ],
    },
};

export default widgetModule;
